import {
  cloneDeepWith,
  isObject,
  omit,
  groupBy,
  entries,
  forEach,
  flatMap,
  filter,
  isEmpty,
  map,
  get,
  find,
  cloneDeep,
  castArray,
  toString,
} from 'lodash-es';
import { Tag } from './type';
import { v4 as uuidV4 } from 'uuid';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useCallback } from 'react';
import { useLevel1Tag } from './tagLevel';

const hasLevel3 = (tag: Tag): Tag => {
  if (tag.level === 3) return tag;
  const { subTags, ...other } = tag;
  const filterSubTags = filter(map(subTags, hasLevel3), Boolean);
  if (filterSubTags.length === 0) return undefined!;
  return {
    ...other,
    subTags: filterSubTags,
  };
};

export const cutTag = (data?: Tag[]) => {
  return filter(
    map(
      cloneDeepWith(data || [], (value: Tag) => {
        if (isObject(value) && value.level === 3) {
          return omit(value, ['subTags']);
        }
      }),
      (value) => hasLevel3(value),
    ),
    Boolean,
  );
};

export const mergeIf = (source: Tag[], target?: Tag): Tag[] => {
  if (isEmpty(target)) return source;
  target = cutTag(castArray(target))[0];
  const copySource = cloneDeep(source);
  const level1 = target;
  const level2 = get(level1, 'subTags.0');
  const level3 = get(level2, 'subTags.0');
  const sourceLevel1 = find(copySource, (o) => o.id === level1.id);
  if (sourceLevel1) {
    const sourceLevel2 = find(sourceLevel1.subTags, (o) => o.id === level2.id);
    if (sourceLevel2) {
      const sourceLevel3 = find(sourceLevel2.subTags, (o) => o.id === level3.id);
      if (!sourceLevel3) {
        sourceLevel2.subTags?.push(level3);
      }
    } else {
      sourceLevel1.subTags?.push(level2);
    }
  } else {
    copySource.push(target);
  }

  return copySource;
};

export type Option = {
  name: string;
  id: string;
  subTags: Option[];
};

export const groupTagByName = (data?: Tag[]): [Option[], Record<string, number[]>] => {
  const group1 = groupBy(data, 'name');
  const idMap: Record<string, number[]> = {};
  const result: Option[] = [];
  forEach(entries(group1), ([name, tags]) => {
    const uuid = uuidV4();
    idMap[uuid] = map(tags, 'id');
    const children = flatMap(tags, 'subTags');
    if (children?.length) {
      result.push({
        id: uuid,
        name: name,
        subTags: children,
      });
    }
  });

  return [result, idMap];
};

export const safeParse = (str: string): any => {
  str = toString(str);
  try {
    return JSON.parse(str);
  } catch (_) {
    return {};
  }
};

export const useTagTransName = () => {
  const lang = useSystemLanguageValue();
  const level1 = useLevel1Tag();
  return useCallback(
    (tag: Tag | Tag[] | undefined): Tag[] => {
      if (tag == null) return [] as Tag[];
      tag = castArray(tag);
      if (lang === 'zh-CN') return tag;
      const replaceName = (tag: Tag[]): Tag[] => {
        return cloneDeepWith<Tag[]>(tag, (o) => {
          if (o?.level === 1) {
            return {
              ...o,
              name: level1[o.name],
              subTags: replaceName(o.subTags),
            };
          }
          if (o?.level === 2 || o?.level === 3) {
            const { multiLang, ...rest } = o;
            const newName = safeParse(multiLang)['en-US'];
            return {
              ...rest,
              name: isEmpty(newName) ? o.name : newName,
              subTags: replaceName(o.subTags),
            };
          }
        });
      };

      return replaceName(tag);
    },
    [level1, lang],
  );
};

export const extractTagName = (tag: Tag): string[] => {
  const result: string[] = [];
  const extract = (tag: Tag) => {
    tag.level !== 1 && result.push(tag.name);
    forEach(tag.subTags, (sub) => {
      extract(sub);
    });
  };
  extract(tag);
  return result;
};
